/**
 * Ícones da navegação — traço único, inspirados em SF Symbols.
 * Inline em SVG: sem requisição extra, herdam `currentColor` e escalam sem borrar.
 */

import type { ReactElement } from 'react';

type IconProps = { className?: string };

const base = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

export type IconName =
  | 'dashboard' | 'analytics' | 'users' | 'payments' | 'support' | 'licenses' | 'coupons'
  | 'marketing' | 'affiliates' | 'linktree' | 'ai' | 'supportAi' | 'access'
  | 'releases' | 'logout' | 'chevronLeft' | 'search' | 'refresh';

const PATHS: Record<IconName, ReactElement> = {
  dashboard: (
    <>
      <rect x="3" y="3" width="7.5" height="8.5" rx="2" />
      <rect x="13.5" y="3" width="7.5" height="5.5" rx="2" />
      <rect x="3" y="14.5" width="7.5" height="6.5" rx="2" />
      <rect x="13.5" y="11.5" width="7.5" height="9.5" rx="2" />
    </>
  ),
  analytics: (
    <>
      <path d="M4 19V5" />
      <path d="M4 19h16" />
      <path d="M8 17V11" />
      <path d="M12 17V7" />
      <path d="M16 17v-4" />
      <path d="M20 17v-7" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3.4" />
      <path d="M2.8 20c0-3.4 2.8-5.6 6.2-5.6s6.2 2.2 6.2 5.6" />
      <path d="M16.5 5.2a3.2 3.2 0 0 1 0 6" />
      <path d="M18 14.8c2 .7 3.3 2.4 3.3 4.6" />
    </>
  ),
  payments: (
    <>
      <rect x="2.5" y="5" width="19" height="14" rx="3" />
      <path d="M2.5 10h19" />
      <path d="M6.5 14.8h3.5" />
    </>
  ),
  support: (
    <>
      <path d="M20.5 12.2c0 4-3.8 7.2-8.5 7.2a9.7 9.7 0 0 1-2.7-.4L4 21l1.3-3.6A6.9 6.9 0 0 1 3.5 12.2C3.5 8.2 7.3 5 12 5s8.5 3.2 8.5 7.2Z" />
      <path d="M9.7 10.3a2.3 2.3 0 0 1 4.4.8c0 1.5-2.1 1.9-2.1 3.1" />
      <path d="M12 16.6h.01" />
    </>
  ),
  licenses: (
    <>
      <circle cx="7.8" cy="12" r="3.8" />
      <path d="M11.6 12H21" />
      <path d="M17.5 12v3.2" />
      <path d="M20.4 12v2.2" />
    </>
  ),
  coupons: (
    <>
      <path d="M3 9.2V6.5a1.5 1.5 0 0 1 1.5-1.5h15A1.5 1.5 0 0 1 21 6.5v2.7a2.8 2.8 0 0 0 0 5.6v2.7a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 17.5v-2.7a2.8 2.8 0 0 0 0-5.6Z" />
      <path d="M14 8.5v1.8M14 13.7v1.8" />
    </>
  ),
  marketing: (
    <>
      <path d="M3.5 10v4a1.5 1.5 0 0 0 1.5 1.5h2L14 20V4L7 8.5H5A1.5 1.5 0 0 0 3.5 10Z" />
      <path d="M17.8 9a4.2 4.2 0 0 1 0 6" />
      <path d="M20.3 6.4a7.6 7.6 0 0 1 0 11.2" />
    </>
  ),
  affiliates: (
    <>
      <circle cx="17.5" cy="5.5" r="2.8" />
      <circle cx="6" cy="12" r="2.8" />
      <circle cx="17.5" cy="18.5" r="2.8" />
      <path d="M8.5 10.6 15 6.9M8.5 13.4 15 17.1" />
    </>
  ),
  linktree: (
    <>
      <path d="M10.2 13.8a4 4 0 0 0 6 .5l2.5-2.5a4 4 0 0 0-5.7-5.7L11.6 7.5" />
      <path d="M13.8 10.2a4 4 0 0 0-6-.5l-2.5 2.5a4 4 0 0 0 5.7 5.7l1.4-1.4" />
    </>
  ),
  ai: (
    <>
      <path d="M12 3.2 13.7 8 18.5 9.7 13.7 11.4 12 16.2 10.3 11.4 5.5 9.7 10.3 8Z" />
      <path d="M18.2 15.5 19 17.6l2.1.8-2.1.8-.8 2.1-.8-2.1-2.1-.8 2.1-.8Z" />
    </>
  ),
  supportAi: (
    <>
      <path d="M20.5 11.8c0 3.9-3.8 7-8.5 7a9.7 9.7 0 0 1-2.7-.4L4 20.5l1.3-3.5a6.8 6.8 0 0 1-1.8-5.2C3.5 8 7.3 4.8 12 4.8s8.5 3.2 8.5 7Z" />
      <path d="M12 7.8 13 10.6l2.8 1-2.8 1-1 2.8-1-2.8-2.8-1 2.8-1Z" />
    </>
  ),
  access: (
    <>
      <rect x="4.5" y="10.2" width="15" height="10.3" rx="2.5" />
      <path d="M8 10.2V7.6a4 4 0 0 1 8 0v2.6" />
      <path d="M12 14.4v2.2" />
    </>
  ),
  releases: (
    <>
      <path d="M12 2.8 20.5 7.4v9.2L12 21.2 3.5 16.6V7.4Z" />
      <path d="M3.8 7.6 12 12l8.2-4.4M12 12v9" />
    </>
  ),
  logout: (
    <>
      <path d="M15 4.8h3.2A1.8 1.8 0 0 1 20 6.6v10.8a1.8 1.8 0 0 1-1.8 1.8H15" />
      <path d="M10 15.5 13.5 12 10 8.5" />
      <path d="M13.5 12H4" />
    </>
  ),
  chevronLeft: <path d="M14.5 5.5 8 12l6.5 6.5" />,
  search: (
    <>
      <circle cx="10.8" cy="10.8" r="6.3" />
      <path d="M15.5 15.5 20.5 20.5" />
    </>
  ),
  refresh: (
    <>
      <path d="M20.3 12a8.3 8.3 0 1 1-2.4-5.9" />
      <path d="M20.5 4.2v4.6h-4.6" />
    </>
  ),
};

export function Icon({ name, className = 'nav-icon' }: IconProps & { name: IconName }) {
  return (
    <svg {...base} className={className} aria-hidden="true" focusable="false">
      {PATHS[name]}
    </svg>
  );
}
